import { IAlertResponseTime } from "app/models/statistics.model";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { TranslateModule } from "@ngx-translate/core";
import { DeviceTypePipe, MomentPipe, SecondsToReadableFormatPipe } from "app/pipes/pipes";
import {
  Component,
  OnInit,
  ViewChild,
  Input,
  AfterViewInit,
  ChangeDetectionStrategy,
  ElementRef,
  HostListener,
} from "@angular/core";
import { NgIf } from "@angular/common";
import { Angular2Csv } from "angular2-csv";
import moment from "moment-timezone";
import { HelperService } from "app/services/helper.service";
import { StatisticsService } from "app/services/statistics.service";
import { csvTextCell, sanitizeCsvCell } from "app/shared/csv.shared";
import {
  StatisticsExportSnapshot,
  StatisticsExportWidget,
} from "app/services/statistics-export.models";

const CSV_HEADERS = ["Timestamp", "Device name", "Device type", "Serial number", "Response time"];
const CSV_OPTIONS = { fieldSeparator: ";", quoteStrings: '"', decimalseparator: ".", showTitle: false, headers: CSV_HEADERS };

@Component({
  selector: "alert-response-times",
  templateUrl: "./alert-response-times.component.html",
  styleUrls: ["./alert-response-times.component.css"],
  standalone: true,
  imports: [
    TranslateModule,
    SecondsToReadableFormatPipe,
    MatTableModule,
    MatSortModule,
    MomentPipe,
    DeviceTypePipe,
    NgIf,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertResponseTimesComponent implements OnInit, AfterViewInit, StatisticsExportWidget {
  constructor(
    private helper: HelperService,
    private statistics: StatisticsService
  ) {}

  @Input() responseTimes: IAlertResponseTime[];

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("dropdownBtn") dropdownBtn: ElementRef;
  @ViewChild("dropdownMenu") dropdownMenu: ElementRef;

  showDropdownMenu: boolean = false;

  tableData: MatTableDataSource<IAlertResponseTime>;

  displayedColumns: string[] = [
    "timestamp",
    "deviceName",
    "deviceType",
    "deviceId",
    "responseTime",
  ];

  /**
   * Hides the export dropdown when clicking outside of it
   * @param event Click event
   */
  @HostListener("document:click", ["$event"])
  handleHideDropdown(event: Event): void {
    if (this.dropdownBtn && this.showDropdownMenu) {
      const isOption = this.dropdownMenu?.nativeElement.contains(event.target);
      const isDropdownBtn = this.dropdownBtn.nativeElement === event.target;
      const isBtnIcon = this.dropdownBtn.nativeElement.contains(event.target);

      if (!isDropdownBtn && !isBtnIcon && !isOption) {
        this.showDropdownMenu = false;
      }
    }
  }

  /**
   * Exports the rows shown in the table as CSV. The data already reflects the
   * filters applied in the Alarm center tab (it is fetched with them), and the
   * rows are written in the table's current sort order.
   */
  exportCSV(): void {
    const startDate = moment(this.statistics.filters.startDate).format("YYYY-MM-DD");
    const endDate = moment(this.statistics.filters.endDate).format("YYYY-MM-DD");

    const filename = "alertResponseTimes_" + startDate + "_" + endDate;
    const csvData = this.parseCsv(this.getExportRows());

    // Generate the CSV file, send it to the browser
    new Angular2Csv(csvData, filename, CSV_OPTIONS);

    // Close the dropdown menu after exporting the CSV
    this.showDropdownMenu = false;
  }

  parseCsv(responseTimes: IAlertResponseTime[]): any[] {
    return responseTimes.map((row) => ({
      timestamp: this.helper.formatDate(row.timestamp, "l LTS"),
      // Neutralize user-entered names so spreadsheets don't evaluate them as formulas
      deviceName: sanitizeCsvCell(row.deviceName ?? ""),
      deviceType: this.helper.getDeviceType(row.deviceType),
      // Text formula keeps leading zeros in serial numbers (quotes escaped)
      deviceId: csvTextCell(row.deviceId),
      responseTime: this.helper.convertSecondsToReadableFormat(row.responseTime),
    }));
  }

  /** Rows in the same order as currently shown in the table. */
  private getExportRows(): IAlertResponseTime[] {
    const rows = [...(this.responseTimes ?? [])];
    if (this.tableData && this.sort) {
      return this.tableData.sortData(rows, this.sort);
    }
    return rows;
  }

  ngAfterViewInit(): void {
    this.tableData.sort = this.sort;
  }

  ngOnInit(): void {
    this.tableData = new MatTableDataSource(this.responseTimes);
  }

  // PNG download is disabled for this list widget; only the PDF export consumes
  // this snapshot, which embeds the structured native table (paginates
  // automatically as vector text).
  async captureForExport(): Promise<StatisticsExportSnapshot | null> {
    return this.buildNativeTableSnapshot();
  }

  private buildNativeTableSnapshot(): StatisticsExportSnapshot {
    const rows = (this.responseTimes ?? []).map((row) => ({
      cells: [
        this.helper.formatDate(row.timestamp, "l LTS"),
        row.deviceName ?? "",
        this.helper.getDeviceType(row.deviceType),
        row.deviceId ?? "",
        this.helper.convertSecondsToReadableFormat(row.responseTime),
      ],
    }));

    return {
      slug: "alert-response-times",
      titleKey: "RESPONSE_TIME_PER_ALERT",
      imageDataUrl: "",
      pdfNativeTable: {
        columns: [
          { headerKey: "TIMESTAMP", align: "left", width: "auto" },
          { headerKey: "DEVICE_NAME", align: "left", width: "*" },
          { headerKey: "DEVICE_TYPE", align: "left", width: "auto" },
          { headerKey: "SERIAL_NUMBER", align: "left", width: "auto" },
          { headerKey: "RESPONSE_TIME_TEXT", align: "right", width: "auto" },
        ],
        rows,
      },
    };
  }
}
